// Destructive transposition (§1.5, §4.3).

import type { Draft } from 'immer';
import { type Pitch, transposePitchEx, transposeKeyFifths, transposeChordName } from '@simplenotation/theory';
import type { Score, BeatRef } from '../model/types';
import { keySigAt, voiceOf } from '../model/query';
import { type EditorState, mutate, selectedRefs, selectionRange, refingerBeat, retabKeepStrings, draftBeat } from './state';

export interface TransposeOptions {
  scope: 'selection' | 'track' | 'all';
  /** Track for scope 'track' (defaults to the cursor track). */
  track?: number;
  intervalIdx: number;
  dir: 1 | -1;
  includeKeySigs: boolean;
  includeChordNames: boolean;
  /** Re-finger every note instead of keeping strings ("prefer low positions"). */
  refingerAll?: boolean;
}

/** Key signatures can only follow a transposition that covers every track (they are global). */
export function canTransposeKeySigs(st: EditorState, opts: Pick<TransposeOptions, 'scope'>): boolean {
  if (st.score.tracks.length === 1) return true;
  if (opts.scope === 'all') return true;
  if (opts.scope === 'selection') {
    const r = selectionRange(st);
    return r.trackFrom === 0 && r.trackTo === st.score.tracks.length - 1;
  }
  return false;
}

function allRefs(score: Score, tracks: number[]): BeatRef[] {
  const out: BeatRef[] = [];
  for (const t of tracks) {
    score.tracks[t].bars.forEach((tb, bar) => {
      tb.voices.forEach((v, voice) => {
        v.forEach((_, beat) => out.push({ track: t, bar, voice, beat }));
      });
    });
  }
  return out;
}

export function transposeScore(st: EditorState, opts: TransposeOptions): EditorState {
  const { intervalIdx, dir } = opts;
  let refs: BeatRef[];
  let barFrom = 0;
  let barTo = st.score.bars.length - 1;
  if (opts.scope === 'selection') {
    // A selection spanning all voices of the selected beats: the cursor voice only (like the editor).
    refs = selectedRefs(st);
    const r = selectionRange(st);
    barFrom = r.start.bar;
    barTo = r.end.bar;
  } else if (opts.scope === 'track') {
    refs = allRefs(st.score, [opts.track ?? st.cursor.track]);
  } else {
    refs = allRefs(
      st.score,
      st.score.tracks.map((_, i) => i),
    );
  }
  const doKeys = opts.includeKeySigs && canTransposeKeySigs(st, opts);

  return mutate(st, (d) => {
    // Enharmonic shift (multiple of 12 fifths) applied when a key normalises, per bar.
    const shiftByBar = new Map<number, number>();
    if (doKeys) {
      const n = d.bars.length;
      const restore = opts.scope === 'selection' && barTo + 1 < n && !d.bars[barTo + 1].keySig ? keySigAt(d as Score, barTo + 1) : null;
      const startKey = keySigAt(d as Score, barFrom);
      let current = transposeKeyFifths(startKey.fifths, intervalIdx, dir);
      const firstExplicit = !!d.bars[barFrom].keySig;
      if (!firstExplicit) d.bars[barFrom].keySig = { fifths: current.fifths, mode: startKey.mode };
      for (let i = barFrom; i <= barTo; i++) {
        const ks = d.bars[i].keySig;
        if (ks && (i > barFrom || firstExplicit)) {
          current = transposeKeyFifths(ks.fifths, intervalIdx, dir);
          ks.fifths = current.fifths;
        }
        shiftByBar.set(i, current.shift);
      }
      if (restore) d.bars[barTo + 1].keySig = { ...restore };
    }
    const touched = new Set<string>();
    for (const ref of refs) {
      const b = draftBeat(d, ref);
      if (!b) continue;
      if (!b.rest) {
        const shift = shiftByBar.get(ref.bar) ?? 0;
        for (const note of b.notes) {
          const orig = note.pitch as Pitch;
          let p = transposePitchEx(orig, intervalIdx, dir).pitch;
          if (shift !== 0) {
            const q = transposePitchEx(p, 1, shift < 0 ? 1 : -1).pitch;
            if (Math.abs(q.alter) <= 1 || Math.abs(p.alter) === 2) p = q;
          }
          note.pitch = p;
        }
      }
      if (opts.includeChordNames && b.chord?.name) {
        b.chord.name = transposeChordName(b.chord.name, intervalIdx, dir);
        delete b.chord.diagram;
      }
      touched.add(`${ref.track}:${ref.bar}:${ref.voice}:${ref.beat}`);
    }
    // Re-tab in document order so hand-position context flows naturally.
    const ordered = [...refs].sort((a, b) => a.track - b.track || a.voice - b.voice || a.bar - b.bar || a.beat - b.beat);
    for (const ref of ordered) {
      if (opts.refingerAll) refingerBeat(d as Draft<Score>, ref);
      else retabKeepStrings(d as Draft<Score>, ref);
    }
  });
}

/** Number of notes a transpose would touch (for the dialog's summary). */
export function countTransposeTargets(st: EditorState, scope: TransposeOptions['scope'], track?: number): number {
  const refs =
    scope === 'selection'
      ? selectedRefs(st)
      : allRefs(st.score, scope === 'track' ? [track ?? st.cursor.track] : st.score.tracks.map((_, i) => i));
  let n = 0;
  for (const r of refs) {
    const b = voiceOf(st.score, r)[r.beat];
    if (b && !b.rest) n += b.notes.length;
  }
  return n;
}
